<script>
import UserResource from '../resources/user'
import ImageResource from '../resources/image'
import SkillResource from '../resources/skill'
import messages from '../mixins/messages'
import LoginForm from '../components/LoginForm.vue'
import CompanyRegisterForm from '../components/CompanyRegisterForm.vue'
import JobPostingForm from '../components/JobPostingForm.vue'
import JobPosting from '../components/JobPosting.vue'
import CompanyResource from '../resources/company'
import ImageUpload from '../components/ImageUpload.vue'
import ImageAvatar from '../components/ImageAvatar.vue'
import RegionSelect from '../components/RegionSelect.vue'
import ResourceSelect from '../components/ResourceSelect.vue'
import RoleResource from '../resources/role'
import MetroAreaResource from '../resources/metroarea'
import ResourceSearch from '../components/ResourceSearch.vue'
import ExperienceResource from '../resources/experience'
import DeveloperProfileResource from '../resources/developerprofile'
import EducationResource from '../resources/education'
import { simpleSafeStringToHTML, currencyUSDFormat } from '../util/string'
import { mapGetters } from '../store'
import { MONTH_MAP } from '../config'

export default {
    name: 'SignupDeveloperProfile',
    mixins: [messages],
    components: {
        RegionSelect,
        ResourceSelect,
        ResourceSearch,
        ImageAvatar,
        ImageUpload
    },
    computed: {
        ...mapGetters(['userResource']),
        bioCharsMaxLength() {
            return 1400
        },
        bioCharsRemaining() {
            return Math.abs((this.profileModel.bio || '').length - this.bioCharsMaxLength)
        },
        experienceDescCharsMaxLength() {
            return 1400
        },
        experienceDescCharsRemaining() {
            return Math.abs((this.experienceModel.description || '').length - this.experienceDescCharsMaxLength)
        },
        monthItems() {
            return MONTH_MAP
        },
        selectedSkillIds() {
            return this.profileModel.skills.map((resource) => resource.id)
        },
        salaryPretty() {
            if(this.profileModel.salary) {
                return currencyUSDFormat(this.profileModel.salary).replace(/\D00(?=\D*$)/, '')
            }

            return ''
        }
    },
    data() {
        return {
            loading: false,
            saving: false,
            showAddEducationModal: false,
            showAddExperienceModal: false,
            profileFormState: {},
            educationFormState: {},
            experienceFormState: {},
            suggestedRoleResources: [],
            profileResource: new DeveloperProfileResource(),
            imageResource: undefined,
            profileModel: {
                rolePrimary: undefined,
                lookingFor: undefined,
                rolesSecondary: [],
                yearsExperience: 1,
                experience: [],
                education: [],
                skills: [],
                remotePreference: 0,
                authorized: true,
                sponsorshipRequired: false
            },
            educationModel: {
                school: undefined
            },
            experienceModel: {
                company: ''
            }
        }
    },
    watch: {
        'profileModel.rolePrimary': function() {
            this.updateSuggestedSkills()
        },
        userResource() {
            this.inflateFormFromProfileResource()
        }
    },
    methods: {
        async onSubmit() {
            if(this.profileFormState.$valid) {
                try {
                    this.saving = true

                    let attrs = {
                        user: this.userResource.id,
                        headline: this.profileModel.bio,
                        location_preferred: this.profileModel.region.id,
                        primary_role: this.profileModel.rolePrimary.id,
                        secondary_roles: this.profileModel.rolesSecondary.map((resource) => resource.id),
                        skills: this.profileModel.skills.map((resource) => resource.id),
                        years_experience: this.profileModel.yearsExperience,
                        looking_for: [this.profileModel.lookingFor.id],
                        authorized: this.profileModel.authorized,
                        sponsorship_required: this.profileModel.sponsorshipRequired,
                        remote_preference: this.profileModel.remotePreference,
                        salary: this.profileModel.salary
                    }

                    for(let key in attrs) {
                        this.profileResource.set(key, attrs[key])
                    }

                    // Save profile
                    await this.profileResource.save()

                    const setProfileIDThenSave = (resource) => {
                        // Set new profile id onto existing relationships
                        resource.attributes.profile = this.profileResource.id
                        // And then save
                        return resource.save()
                    }
                    
                    await Promise.all(this.profileModel.education.map(setProfileIDThenSave))
                    await Promise.all(this.profileModel.experience.map(setProfileIDThenSave))

                    // Image
                    if(this.imageResource) {
                        await this.imageResource.save()
                        this.profileResource.set('image', this.imageResource.id)
                        await this.profileResource.save()
                    }

                    this.successRedirect()
                } catch(e) {
                    debugger
                    this.addError(this.parseAPIErrorsOrRethrow(e))
                } finally {
                    this.saving = false
                }
            }
        },
        async inflateFormFromProfileResource() {
            this.loading = true
            let profile = await this.userResource.getProfile()

            if(!profile) {
                this.loading = false
                return false
            }
            
            this.profileResource = profile
            await this.profileResource.resolveRelated()
            await this.profileResource.resolveAttribute('education.school.name')

            this.profileModel = {
                bio: this.profileResource.attributes.headline,
                region: this.profileResource.rel('location_preferred').resource,
                rolePrimary: this.profileResource.rel('primary_role').resource,
                rolesSecondary: this.profileResource.rel('secondary_roles').resources,
                yearsExperience: (this.profileResource.attributes.years_experience >= 15) ? 15 : this.profileResource.attributes.years_experience,
                lookingFor: this.profileResource.rel('looking_for').resource,
                authorized: this.profileResource.attributes.authorized,
                sponsorshipRequired: this.profileResource.attributes.sponsorship_required,
                remotePreference: this.profileResource.attributes.remote_preference,
                salary: this.profileResource.attributes.salary,
                education: this.profileResource.rel('education').resources,
                experience: this.profileResource.rel('experience').resources,
                skills: this.profileResource.rel('skills').resources,
            }

            this.imageResource = this.profileResource.rel('image').resource

            this.loading = false
        },
        successRedirect() {
            this.$router.push({ name: 'home' })
        },
        onCropperSave(dataURL) {
            this.imageResource = ImageResource.fromDataURL(dataURL)
        },
        async addEducation() {
            if(this.educationFormState.$valid) {
                let education = new EducationResource({
                    school: this.educationModel.school.id,
                    degree: this.educationModel.degree,
                    concentration: this.educationModel.concentration,
                    start_year: this.educationModel.startYear,
                    end_year: this.educationModel.endYear,
                    readonly_values: {
                        school_name: String(this.educationModel.school)
                    }
                })

                let schoolManager = education.rel('school')

                schoolManager._resources[this.educationModel.school.id] = this.educationModel.school
                schoolManager.resolved = true
                
                this.profileModel.education.push(education)
                this.educationModel = {
                    school: undefined
                }

                this.educationFormState.$submitted = false
                this.educationFormState.$valid = false
            }
        },
        selectSkill(skillResource) {
            this.profileModel.skills.push(skillResource)
            this.updateSuggestedSkills()
        },
        async updateSuggestedSkills() {
            if(!this.profileModel.rolePrimary) {
                return false
            }

            let result = await SkillResource.filter({ roles: this.profileModel.rolePrimary.id })
            this.suggestedRoleResources = result.resources.filter((resource) => !~this.selectedSkillIds.indexOf(resource.id)).slice(0, 5)
        },
        async removeImage() {
            if(!this.imageResource.isNew() && confirm('Are you sure you want to remove this image? This action cannot be undone')) {
                await this.imageResource.delete()
            }

            this.imageResource = undefined
        },
        addEducationAndClose() {
            if(this.educationFormState.$valid) {
                this.addEducation()
                this.showAddEducationModal = false
            }
        },
        addExperience() {
            if(this.experienceFormState.$valid) {
                let experience = new ExperienceResource({
                    company_name: this.experienceModel.companyName,
                    location: this.experienceModel.location,
                    title: this.experienceModel.title,
                    start_month: this.experienceModel.startMonth,
                    start_year: this.experienceModel.startYear,
                    end_month: this.experienceModel.endMonth,
                    end_year: this.experienceModel.endYear,
                    description: this.experienceModel.description
                })
                
                this.profileModel.experience.push(experience)
                this.experienceModel = {}

                this.experienceFormState.$submitted = false
                this.experienceFormState.$valid = false
            }
        },
        addExperienceAndClose() {
            if(this.experienceFormState.$valid) {
                this.addExperience()
                this.showAddExperienceModal = false
            }
        },
        simpleSafeStringToHTML(content) {
            return simpleSafeStringToHTML(content)
        },
        removeNodeFrom(list, itemInList) {
            let index = list.indexOf(itemInList)
            list.splice(index, 1)
        },
        async confirmRemoveNodeFrom(list, itemInList) {
            if(itemInList.isNew()) {
                this.removeNodeFrom(list, itemInList)
            } else if(!itemInList.isNew() && confirm('Are you sure you want to remove this item? This action cannot be undone.')) {
                await itemInList.delete()
                this.removeNodeFrom(list, itemInList)
            }
        }
    },
    async beforeMount() {
        if(!this.profileModel.lookingFor) {
            this.profileModel.lookingFor = await this.$resources.LookingForResource.detail('fulltime')
        }
    },
    async mounted() {
        if(this.userResource) {
            this.inflateFormFromProfileResource()
        }
    },
}
</script>

<template>
    <div v-page-title="'Create a Developer Profile'">
        <section>
            <div class="section-heading text-center mb-0 mt-5">
                <p>Create a Profile</p>
                <h2>Stand Out to Thousands of Startups</h2>
            </div>
        </section>

        <section class="p-md-5 mt-5 gray" v-if="!loading">
            <b-container>
                <vue-form :state="profileFormState" @submit.prevent="onSubmit">
                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>About You</h4>
                            <p>Tell prospective companies a little about yourself and what you're looking for.</p>
                        </b-col>
                        <b-col>
                            <!-- Form Group -->
                            <b-card>

                                <b-row>
                                    <b-col>
                                        <validate auto-label>
                                            <b-form-group label="Region">
                                                <region-select v-model="profileModel.region" name="region" single required />
                                                <field-messages name="region" show="$submitted">
                                                    <small slot="required" class="text-danger">Region is required</small>
                                                </field-messages>
                                            </b-form-group>
                                        </validate>
                                    </b-col>

                                    <b-col>
                                        <validate auto-label>
                                            <b-form-group label="Looking For">
                                                <resource-select v-model="profileModel.lookingFor" :resource-class="$resources.LookingForResource" name="lookingFor" required />
                                                <field-messages name="lookingFor" show="$submitted">
                                                    <small slot="required" class="text-danger">Required</small>
                                                </field-messages>
                                            </b-form-group>
                                        </validate>
                                    </b-col>
                                </b-row>

                                <b-row>
                                    <b-col>
                                        <validate auto-label>
                                            <b-form-group label="Primary Role">
                                                <resource-select v-model="profileModel.rolePrimary" :resource-class="$resources.RoleResource" required name="role" />
                                                <field-messages name="role" show="$submitted">
                                                    <small slot="required" class="text-danger">Required</small>
                                                </field-messages>
                                            </b-form-group>
                                        </validate>
                                    </b-col>
                                    <b-col>
                                        <validate auto-label>
                                            <b-form-group label="Remote Preference">
                                                <b-form-select v-model="profileModel.remotePreference" name="remote">
                                                    <b-form-select-option :value="0">No Preference</b-form-select-option>
                                                    <b-form-select-option :value="1">Never</b-form-select-option>
                                                    <b-form-select-option :value="2">Sometimes</b-form-select-option>
                                                    <b-form-select-option :value="3">Most Days</b-form-select-option>
                                                    <b-form-select-option :value="4">Remote Only</b-form-select-option>
                                                </b-form-select>
                                                <field-messages name="remote" show="$submitted">
                                                    <small slot="required" class="text-danger">Required</small>
                                                </field-messages>
                                            </b-form-group>
                                        </validate>
                                    </b-col>
                                </b-row>

                                <b-row v-if="profileModel.rolePrimary">
                                    <b-col>
                                        <validate auto-label>
                                            <b-form-group label="Experience" :description="`How many years experience as a ${profileModel.rolePrimary}`">
                                                <b-form-select v-model="profileModel.yearsExperience" name="experience" class="col-md-6">
                                                    <b-form-select-option v-for="(unused, i) in new Array(14)" :key="i" :value="i + 1">{{ i + 1 }}</b-form-select-option>
                                                    <b-form-select-option :value="15">15+</b-form-select-option>
                                                </b-form-select>
                                                <field-messages name="experience" show="$submitted">
                                                    <small slot="required" class="text-danger">Required</small>
                                                </field-messages>
                                            </b-form-group>
                                        </validate>
                                    </b-col>
                                </b-row>

                                <validate auto-label>
                                    <b-form-group label="Other Roles You're Open To" description="Hold down the command button to select multiple items">
                                        <resource-select
                                            :resource-class="$resources.RoleResource"
                                            v-model="profileModel.rolesSecondary"
                                            multiple
                                            name="rolesSecondary">
                                        </resource-select>
                                        <field-messages name="rolesSecondary" show="$submitted">
                                            <small slot="required" class="text-danger">Required</small>
                                        </field-messages>
                                        <div class="selected-secondary-roles">
                                            <b-badge
                                                v-for="(resource, i) in profileModel.rolesSecondary"
                                                :key="i"
                                                variant="secondary"
                                                class="mr-1 mb-1"
                                                @click="removeNodeFrom(profileModel.rolesSecondary, resource)"
                                                href="javascript:{}">
                                                {{ resource }}
                                            </b-badge>
                                        </div>
                                    </b-form-group>
                                </validate>

                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3" v-if="profileModel.lookingFor && profileModel.lookingFor.id === 'fulltime'">
                        <b-col md="4">
                            <h4>Salary</h4>
                            <p>Tell us how much you'd like to make.</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <b-row>
                                    <b-col>
                                        <b-form-group label="Salary Requirements">
                                            <small class="form-text text-muted">If you are a contractor or looking for an internship, you can change this field in the "Looking For" section above</small>
                                            <b-form-input v-model="profileModel.salary" type="range" min="50000" max="500000" step="1000" name="salary"></b-form-input>
                                            <strong v-text="salaryPretty" v-if="profileModel.salary" />
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>Education</h4>
                            <p>What schools have you studied at?</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <b-card v-for="(education, i) in profileModel.education" :key="i" class="edu-exp mb-3">
                                    <b-button variant="link" class="float-right" @click="confirmRemoveNodeFrom(profileModel.education, education)"><b-icon icon="x" /></b-button>
                                    <p class="lead mb-0"><b-icon icon="award-fill" /> {{ education.get('school.name') }}</p>
                                    <p class="mb-0">
                                        <small>
                                            <strong>{{ education.attributes.concentration }}</strong> — {{ education.attributes.degree }}
                                            <span v-if="education.dateSpanPretty"> — <em class="text-muted">{{ education.dateSpanPretty }}</em></span>
                                        </small>
                                    </p>
                                </b-card>
                                <div v-if="profileModel.education.length === 0" class="empty-edu-exp">
                                    Click "Add School" to start adding
                                </div>
                                <b-button variant="secondary" @click="showAddEducationModal = true"><b-icon icon="patch-plus" /> Add School</b-button>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>Work Experience</h4>
                            <p>What positions have you held?</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <b-card v-for="(experience, i) in profileModel.experience" :key="i" class="edu-exp mb-3">
                                    <b-row>
                                        <b-col sm="2">
                                            <b-avatar size="4rem" variant="dark" icon="building" rounded />
                                        </b-col>
                                        <b-col>
                                            <b-button variant="link" class="float-right" @click="confirmRemoveNodeFrom(profileModel.experience, experience)"><b-icon icon="x" /></b-button>
                                            <p class="lead mb-0">{{ experience.attributes.title }}</p>
                                            <p class="mb-0">{{ experience.attributes.company }}</p>
                                            <p class="mb-3 text-muted" v-if="experience.dateSpanPretty || experience.attributes.location">
                                                <small>{{ [experience.dateSpanPretty, experience.attributes.location].filter((value) => !!value).join(' - ') }}</small>
                                            </p>
                                            <p class=""><span v-html="simpleSafeStringToHTML(experience.get('description'))" /></p>
                                        </b-col>
                                    </b-row>
                                </b-card>
                                <div v-if="profileModel.experience.length === 0" class="empty-edu-exp">
                                    Click "Add Experience" to start adding
                                </div>
                                <b-button variant="secondary" @click="showAddExperienceModal = true"><b-icon icon="patch-plus" /> Add Experience</b-button>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>Skills</h4>
                            <p>This will help us match you with the most relevant jobs.</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <div class="mb-3">
                                    <b-badge v-for="(skill, i) in profileModel.skills" :key="i" href="javascript:{}" variant="secondary" class="mr-1" @click="removeNodeFrom(profileModel.skills, skill)">{{ skill }}</b-badge>
                                </div>
                                <div v-if="profileModel.skills.length === 0" class="empty-edu-exp">
                                    Search for a skill to start adding
                                </div>
                                <b-row>
                                    <b-col>
                                        <resource-search
                                            :resourceClass="$resources.SkillResource"
                                            label="Search for skills to add"
                                            height="250"
                                            placeholder="Start typing to search... (eg. TypeScript)"
                                            :filter="(resource) => !~selectedSkillIds.indexOf(resource.id)"
                                            @select="selectSkill">
                                        </resource-search>
                                    </b-col>
                                    <b-col v-if="profileModel.rolePrimary">
                                        <small class="form-text text-muted">
                                            Suggested Skills for <strong>{{ profileModel.rolePrimary }}</strong>:
                                        </small>
                                        <b-badge
                                            v-for="(suggestedRoleResource, i) in suggestedRoleResources"
                                            :key="i"
                                            variant="secondary"
                                            href="javascript: {}"
                                            @click="selectSkill(suggestedRoleResource)"
                                            class="mr-1 mb-1">
                                            {{ suggestedRoleResource }}
                                        </b-badge>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>Your Biography</h4>
                            <p>Sharing more details about yourself will help you stand out more.</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <validate auto-label>
                                    <b-form-group label="Your Biography">
                                        <b-form-textarea v-model="profileModel.bio" :maxlength="bioCharsMaxLength" rows="6" name="bio" required>
                                        </b-form-textarea>
                                        <field-messages name="bio" show="$submitted">
                                            <small slot="required" class="text-danger">Required</small>
                                        </field-messages>
                                        <template #description>
                                            <b-badge class="float-right" :variant="bioCharsRemaining < 100 ? 'danger' : 'secondary'">{{ bioCharsRemaining }}</b-badge>
                                        </template>
                                    </b-form-group>
                                </validate>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col md="4">
                            <h4>Profile Image</h4>
                            <p>Upload a public photo of yourself.</p>
                        </b-col>
                        <b-col>
                            <b-card>
                                <image-avatar :resource="imageResource" size="6rem" v-if="imageResource" />
                                <image-upload @success="onCropperSave" v-if="!imageResource" />
                                <b-button v-else variant="link" @click="removeImage" >Remove Image</b-button>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3">
                        <b-col offset-md="4">
                            <!-- Form Group -->
                            <b-card>
                                <b-row>
                                    <b-col>
                                        <b-form-group label="Work Status">
                                            <b-form-checkbox v-model="profileModel.authorized" switch>Authorized to work in the United States</b-form-checkbox>
                                            <b-form-checkbox v-model="profileModel.sponsorshipRequired" switch>Sponsorship (eg. H1B) is required</b-form-checkbox>
                                        </b-form-group>
                                    </b-col>
                                </b-row>
                            </b-card>
                        </b-col>
                    </b-row>

                    <b-row class="mb-3 mt-5">
                        <b-col offset="4">
                            <b-button type="submit" size="lg" variant="primary" :disabled="saving">Save Profile and Continue</b-button>
                            <b-alert variant="danger" class="mt-3" :show="profileFormState.$submitted && !profileFormState.$valid">Please correct the errors above</b-alert>
                            <messages />
                        </b-col>
                    </b-row>
                    
                </vue-form>
            </b-container>
        </section>

        <section class="p-md-5 mt-5 gray" v-else>
            <b-container class="text-center mt-5 mb-5">
                <b-card>
                    <b-skeleton width="85%"></b-skeleton>
                    <b-skeleton width="55%"></b-skeleton>
                    <b-skeleton width="70%"></b-skeleton>
                </b-card>
                <b-card class="mt-3">
                    <b-skeleton width="85%"></b-skeleton>
                    <b-skeleton width="55%"></b-skeleton>
                    <b-skeleton width="70%"></b-skeleton>
                    <b-skeleton width="55%"></b-skeleton>
                    <b-skeleton width="70%"></b-skeleton>
                    <b-skeleton width="55%"></b-skeleton>
                    <b-skeleton width="70%"></b-skeleton>
                </b-card>
            </b-container>
        </section>

        <!-- Modals -->
        <b-modal v-model="showAddEducationModal" title="Add Education" no-fade hide-footer centered>
            <vue-form :state="educationFormState" @submit.prevent="addEducationAndClose" ref="educationForm">
                <validate v-if="!educationModel.school" auto-label>
                    <b-form-group label="School">
                        <resource-search
                            :resource-class="$resources.SchoolResource"
                            v-model="educationModel.school"
                            @select="(resource) => educationModel.school = resource"
                            required
                            name="school">
                        </resource-search>
                        <!--<small v-if="educationFormState.$submitted && !educationModel.school" class="text-danger">Required</small>-->
                        <field-messages name="school" show="$submitted">
                            <small slot="required" class="text-danger">Required</small>
                        </field-messages>
                    </b-form-group>
                </validate>
                <div v-else class="selected-school mb-3">
                    <p class="mb-0 lead">{{ educationModel.school }}</p>
                    <span v-if="educationModel.school.attributes.website">
                        <b-icon icon="link" /> {{ educationModel.school.attributes.website }}
                    </span>
                    <b-button variant="link" class="float-right" @click="educationModel.school = undefined">Change</b-button>
                </div>

                <b-row>
                    <b-col sm="4">
                        <validate auto-label>
                            <b-form-group label="Degree/Cert.">
                                <b-form-input v-model="educationModel.degree" name="degree" required placeholder="Eg. BS, Ph.D" />
                                <field-messages name="degree" show="$submitted">
                                    <small slot="required" class="text-danger">Required</small>
                                </field-messages>
                            </b-form-group>
                        </validate>
                    </b-col>
                    <b-col>
                        <validate auto-label>
                            <b-form-group label="Concentration">
                                <b-form-input v-model="educationModel.concentration" name="concentration" required placeholder="Eg. Computer Science" />
                                <field-messages name="concentration" show="$submitted">
                                    <small slot="required" class="text-danger">Required</small>
                                </field-messages>
                            </b-form-group>
                        </validate>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col sm="4">
                        <validate auto-label>
                            <b-form-group label="Start Year">
                                <b-form-input v-model="educationModel.startYear" name="startYear" />
                                <field-messages name="startYear" show="$submitted">
                                    <small slot="required" class="text-danger">Required</small>
                                </field-messages>
                            </b-form-group>
                        </validate>
                    </b-col>
                    <b-col sm="4">
                        <validate auto-label>
                            <b-form-group label="End Year">
                                <b-form-input v-model="educationModel.endYear" name="endYear" />
                                <field-messages name="endYear" show="$submitted">
                                    <small slot="required" class="text-danger">Required</small>
                                </field-messages>
                            </b-form-group>
                        </validate>
                    </b-col>
                </b-row>

                <hr />

                <b-button-group>
                    <b-button type="submit" variant="primary">Save</b-button>
                    <!--<b-button variant="outline-primary">Save and add another</b-button>-->
                </b-button-group>

            </vue-form>
        </b-modal>

        <b-modal v-model="showAddExperienceModal" title="Add Experience" no-fade hide-footer centered>
            <vue-form :state="experienceFormState" @submit.prevent="addExperienceAndClose">
                <b-row>
                    <b-col>
                        <validate auto-label>
                            <b-form-group label="Company Name">
                                <b-form-input v-model="experienceModel.companyName" name="companyName" required />
                                <field-messages name="companyName" show="$submitted">
                                    <small slot="required" class="text-danger">Required</small>
                                </field-messages>
                            </b-form-group>
                        </validate>
                    </b-col>
                    <b-col>
                        <validate auto-label>
                            <b-form-group label="Location">
                                <b-form-input v-model="experienceModel.location" name="location" />
                            </b-form-group>
                        </validate>
                    </b-col>
                </b-row>
                
                <validate auto-label>
                    <b-form-group label="Title">
                        <b-form-input v-model="experienceModel.title" name="title" required />
                        <field-messages name="title" show="$submitted">
                            <small slot="required" class="text-danger">Required</small>
                        </field-messages>
                    </b-form-group>
                </validate>

                <b-row>
                    <b-col sm="4">
                        <b-form-group label="From">
                            <b-select v-model="experienceModel.startMonth" name="startMonth">
                                <b-select-option :value="undefined">Month</b-select-option>
                                <b-select-option v-for="(month, monthNumber) in monthItems" :key="monthNumber" :value="monthNumber">{{ month }}</b-select-option>
                            </b-select>
                        </b-form-group>
                    </b-col>
                    <b-col sm="4">
                        <b-form-group label="">
                            <template #label>
                                <span class="invisible">Year</span>
                            </template>
                            <b-form-input type="number" v-model="experienceModel.startYear" name="startYear" placeholder="4-Digit Year" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row v-if="!experienceModel.current">
                    <b-col sm="4">
                        <b-form-group label="To">
                            <b-select v-model="experienceModel.endMonth" name="endMonth">
                                <b-select-option :value="undefined">Month</b-select-option>
                                <b-select-option v-for="(month, monthNumber) in monthItems" :key="monthNumber" :value="monthNumber">{{ month }}</b-select-option>
                            </b-select>
                        </b-form-group>
                    </b-col>
                    <b-col sm="4">
                        <b-form-group label="">
                            <template #label>
                                <span class="invisible">Year</span>
                            </template>
                            <b-form-input type="number" v-model="experienceModel.endYear" name="endYear" placeholder="4-Digit Year" />
                        </b-form-group>
                    </b-col>
                </b-row>

                <b-row class="mb-4">
                    <b-col>
                        <b-form-checkbox v-model="experienceModel.current" @input="experienceModel.endYear = experienceModel.endMonth = undefined"> This is a current position</b-form-checkbox>
                    </b-col>
                </b-row>

                <validate auto-label>
                    <b-form-group label="Role Description">
                        <b-form-textarea v-model="experienceModel.description" name="description" rows="3" :maxlength="experienceDescCharsMaxLength" required />
                        <field-messages name="description" show="$submitted">
                            <small slot="required" class="text-danger">Required</small>
                        </field-messages>
                        <template #description>
                            <b-badge class="float-right" :variant="experienceDescCharsRemaining < 100 ? 'danger' : 'secondary'">{{ experienceDescCharsRemaining }}</b-badge>
                        </template>
                    </b-form-group>
                </validate>

                <hr />

                <b-button-group>
                    <b-button type="submit" variant="primary">Save</b-button>
                </b-button-group>

            </vue-form>
        </b-modal>
    </div>
</template>

<style lang="scss">
.selected-school {
    p {
        font-size: 1.4rem;
        font-weight: 700;
    }
}

.edu-exp {
    p.lead {
        font-weight: 700;
    }
}

.empty-edu-exp {
    border: 1px dashed #e8e8e8;
    color: #c5c5c5;
    font-size: 80%;
    font-weight: 400;
    border-radius: 5px;
    padding: 15px;
    margin: 0 0 1rem 0;
}
</style>
